import { useRecoilCallback } from 'recoil'
import {
  additionalFamilyDraftAtom,
  caseFieldsAtom,
  caseValuesAtom,
} from 'atoms'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { sameStringList } from '@/utils/editor/sameStringList'

export type CaseValuePair = { id: string; value: unknown }

const sameCaseValue = (current: unknown, field: CaseValuePair): boolean => {
  if (!current || typeof current !== 'object') return false
  const stored = current as Partial<CaseValuePair>
  return (
    stored.id === field.id &&
    JSON.stringify(stored.value) === JSON.stringify(field.value)
  )
}

/**
 * Loads a case's key/value pairs into the per-field atoms the case form
 * binds to. Only a field whose value changed is written, so a case rewrite
 * that touched one column re-renders one input, not every input on the form.
 */
export const useLoadCaseValues = () =>
  useRecoilCallback(
    ({ set, snapshot }) =>
      (pairs: readonly CaseValuePair[]) => {
        const ids: string[] = []
        for (const field of pairs) {
          ids.push(field.id)
          const current = snapshot
            .getLoadable(caseValuesAtom(field.id))
            .valueMaybe()
          const same = sameCaseValue(current, field)
          countEditorProbeRender(`caseValues:${same ? 'skip' : 'set'}`)
          if (!same) set(caseValuesAtom(field.id), () => field)
        }
        if (
          snapshot.getLoadable(additionalFamilyDraftAtom).valueMaybe() !== null
        )
          set(additionalFamilyDraftAtom, null)
        const currentIds = snapshot.getLoadable(caseFieldsAtom).valueMaybe()
        if (!sameStringList(currentIds as string[] | undefined, ids))
          set(caseFieldsAtom, ids as never[])
      },
    [],
  )

export default useLoadCaseValues
